/**
 * Rebate Only Layout Component
 * Renders only the rebate section when campaignOnly.type === 'rebate' is set.
 *
 * Supports:
 * - campaignOnly: { type: 'rebate', campaignID?: string }
 * - If campaignID is provided, navigates carousel to that specific campaign
 * - If campaignID is not found or not provided, falls back to first campaign
 *
 * Minimal layout with just rebate content
 */

import React, { useCallback, memo } from 'react';
import { SECTION_NAMES } from '@repo/common';

import { useCampaigns, useCampaignActions, useCampaignsStore } from 'features/campaigns/store';
import { CampaignTypeProvider, REBATE_CAMPAIGN_TYPE } from 'features/campaigns';
import { ViewSwitcher } from 'features/navigation';

import { useAppStore } from '../../../store/app/appStore';
import { useCampaignSelection } from '../hooks/useCampaignSelection';

import styles from '../styles/CampaignOnlyLayout.module.css';

interface RebateOnlyLayoutProps {
  campaignID?: string;
}

const RebateOnlyLayout: React.FC<RebateOnlyLayoutProps> = ({ campaignID }) => {
  // Get settings from app store
  const campaignOnly = useAppStore((state) => state.settings.campaignOnly);

  // Show layout if campaignOnly.type='rebate'
  const showRebateOnly = campaignOnly?.type === REBATE_CAMPAIGN_TYPE;

  // Get campaign state
  const selectedCampaignId = useCampaignsStore((state) => state.getSelectedCampaignId(REBATE_CAMPAIGN_TYPE));
  const campaigns = useCampaigns(REBATE_CAMPAIGN_TYPE) as { id: string }[];
  const { setSelectedCampaignId } = useCampaignActions();
  const setActiveRebate = useCallback(
    (id: string) => setSelectedCampaignId(id, REBATE_CAMPAIGN_TYPE),
    [setSelectedCampaignId],
  );

  // Use shared campaign selection hook
  useCampaignSelection({
    shouldShow: showRebateOnly,
    campaignID,
    items: campaigns,
    activeItemId: selectedCampaignId ?? undefined,
    setActiveItem: setActiveRebate,
    sectionName: SECTION_NAMES.REBATE,
    debugPrefix: 'RebateOnly',
    getItemId: (campaign) => campaign.id.toString(),
  });

  // Don't render anything if campaignOnly.type='rebate' is not enabled
  if (!showRebateOnly) {
    return null;
  }

  return (
    <CampaignTypeProvider type={REBATE_CAMPAIGN_TYPE}>
      <div className={styles.campaignOnlyContainer}>
        <div className={styles.contentContainer}>
          {/* Only show rebate content via ViewSwitcher */}
          <ViewSwitcher showTournamentNavigation={false} campaingOnlyView={true} />
        </div>
      </div>
    </CampaignTypeProvider>
  );
};

export default memo(RebateOnlyLayout);
